// The agents' clarification and behavior questions, and the operator's answers.
// Mirrors lambda_handlers/api/stateful_agents/feedback/. See
// docs/Y2026-Q4/design-operator-questions-and-action-provenance.md sections 3-4.
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";

import type { PostJsonError } from "../http/postJson";
import { useAgentsHttp } from "../useAgentsHttp";
import { type DateMoveSubject, type ProjectAgentStatusList } from "./useProjectAgentStatus";
import { type ChangeReview } from "./useActionChanges";
import { QK } from "../queryKeys";
import { agentDisplayLabel } from "../../utils/runParticipants";

export interface FeedbackOption {
  label: string;
  description: string;
}

export type FeedbackStatus = "open" | "answered" | "waiting" | "closed";

/** What an ``amazon_answer`` question is about: Amazon's words as they gave them, and the
 * keeper's view of them (amazon_item_answers._raise / set_check). */
export interface AmazonAnswerSubject {
  type: "amazon_answer";
  answer_kind: "resolved" | "inrange";
  item_text: string;
  site_code: string;
  previous_assignee: string | null;
  exchange: { kind: string; name: string; by: string; when: string; text: string }[];
  /** "pending" while the keeper checks, "done" with its view, "failed" when it could not. */
  check: "pending" | "done" | "failed";
  check_note?: string;
  view: {
    verdict: string;
    records: string;
    other_sites: string;
    recommend: "approve" | "don't approve" | "ask for more";
    why: string;
    draft: string;
  } | null;
}

export interface FeedbackQuestion {
  question_id: string;
  agent: string;
  /** ``date_move``: the platform's question for an agent's proposal to move a date. */
  kind:
    | "clarification"
    | "behavior"
    | "platform"
    | "date_move"
    | "drive_addition"
    | "amazon_answer";
  question: string;
  context: string;
  options: FeedbackOption[];
  multi_select: boolean;
  evidence: string[];
  project_ids: string[];
  project_names: Record<string, string>;
  prompt_document: string | null;
  needed_by: string | null;
  status: FeedbackStatus;
  /** A date move's answer also carries the date it chose and whether it kept the old. */
  answer: {
    options: number[];
    other: string;
    date?: string;
    keep?: boolean;
  } | null;
  /** The record a date move is about, for showing it beside that record. */
  subject: Omit<
    DateMoveSubject,
    "question_id" | "reason" | "proposed_at" | "run_id"
  > | null;
  answered_by: string | null;
  answered_at: string | null;
  review_conversation_id: string | null;
  raised_by_run_id: string;
  closed_by: string | null;
  closed_note: string | null;
  closed_at: string | null;
  closed_in_run_id: string | null;
  stored_in: string | null;
  created_at: string;
  /** The team member's email it was given to, or null. Absent from a server older than
   * assignment. */
  assignee?: string | null;
  /** Open agent feedback only: the sites it shows on, in its own order, each with the
   * specialists whose tab shows it there. */
  sites?: { project_id: string; agents: string[] }[];
  /** The open question this one rewords; a rewording keeps the original's asked date. */
  replaces_question_id: string | null;
  /** ``note`` is an operator's reason for a skip, or what changed since one when the
   * question is asked again (``after_skip``: the skipped question). */
  events: {
    at: string;
    kind: string;
    note?: string;
    after_skip?: string;
    /** ``assigned``: who made it, and the team member it went to ("" for nobody). */
    by?: string;
    to?: string;
  }[];
}

/** An open move of an action's date, answered on the action rather than under Agent
 * feedback; once settled it is history there like any question. Must agree with
 * ``date_moves.on_its_action``, which keeps it out of the feedback count. */
export const isActionDateMove = (
  q: Pick<FeedbackQuestion, "kind" | "subject" | "status">,
): boolean =>
  q.kind === "date_move" &&
  q.status === "open" &&
  q.subject?.item_kind === "action";

/** Waiting on the operator: unanswered, or asked back by the review applying an answer.
 * Must agree with ``needs_operator`` in feedback_waiting.py. */
export const needsOperator = (q: Pick<FeedbackQuestion, "status">): boolean =>
  q.status === "open" || q.status === "waiting";

/** The specialists whose tab shows an Open agent feedback question on any of its sites,
 * by name: a platform fault another specialist met shows on that one's tab. */
export const tabsShowing = (q: Pick<FeedbackQuestion, "sites">): string[] => [
  ...new Set((q.sites ?? []).flatMap((s) => s.agents.map(agentDisplayLabel))),
];

export type FeedbackScope =
  | { project_id: string; agent: string }
  | { prompt_document: string }
  /** The questions one conversation asked, shown under its replies. */
  | { run_id: string }
  /** Every question waiting on an operator or being applied, across the sites the daily
   * run works: My actions' Open agent feedback and its sidebar badge. */
  | { open: true };

const OPEN_SCOPE: FeedbackScope = { open: true };


export interface FeedbackList {
  questions: FeedbackQuestion[];
  /** ``{email: name}``: who a question may be given to (the Team document). Absent from a
   * server older than assignment. */
  team?: Record<string, string>;
}

export const useFeedbackQuestions = (scope: FeedbackScope | null) => {
  const http = useAgentsHttp();
  return useQuery<FeedbackList>(
    [QK.feedbackQuestions, scope],
    () => http<FeedbackList>("/stateful-agents/feedback/list", scope),
    {
      enabled: scope !== null,
      // An answer is applied in the background; its row moves on while the page is open.
      // Quickly only in the first minutes after an answer, so a review that never settles
      // it does not keep the page polling fast. Not Open agent feedback: the nav holds it
      // on every page, and anyone's answer anywhere would set every tab polling fast.
      refetchInterval: (data) =>
        scope !== null && "open" in scope
          ? 60_000
          : data?.questions.some(
          (q) =>
            q.status === "answered" &&
            q.answered_at !== null &&
            Date.now() - new Date(q.answered_at).getTime() < 10 * 60_000,
        )
          ? 5_000
          : 60_000,
    },
  );
};

/** Held by ``viewer`` or by nobody: what the viewer's badge counts and Open agent
 * feedback opens on. */
export const mineOrUnassigned = (
  q: Pick<FeedbackQuestion, "assignee">,
  viewer: string,
): boolean => !q.assignee || q.assignee.toLowerCase() === viewer.toLowerCase();

/** Open agent feedback, and ``waiting``: its questions waiting on an operator that are
 * the viewer's or nobody's, the one count the tab and the sidebar badge show. */
export const useOpenFeedback = (enabled: boolean, viewer: string) => {
  const query = useFeedbackQuestions(enabled ? OPEN_SCOPE : null);
  const waiting = (query.data?.questions ?? []).filter(
    (q) => needsOperator(q) && mineOrUnassigned(q, viewer),
  ).length;
  return { ...query, waiting };
};

/** A team member's name, or their email where they have left the Team list. */
export const teamName = (team: Record<string, string>, email: string): string =>
  team[email] ??
  Object.entries(team).find(([e]) => e.toLowerCase() === email.toLowerCase())?.[1] ??
  email;

/** The latest assignment event on a question: who gave it to whom, and when. */
export const lastAssignment = (
  q: Pick<FeedbackQuestion, "events">,
): { to: string; by: string; at: string } | null => {
  const event = [...q.events].reverse().find((e) => e.kind === "assigned");
  return event
    ? { to: event.to ?? "", by: event.by ?? "", at: event.at }
    : null;
};

export const ASSIGNMENT_MOVED =
  "Not saved: someone changed who holds this question a moment ago. The page now shows who; check and try again.";

/** Give a question to a team member, or to nobody (``assignee: null``). ``expected`` is
 * who the page shows holding it. Every list holding the question shows the new holder at
 * once; the refetch confirms it. */
export const useAssignFeedback = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    { question: SettledQuestion },
    Error,
    { question_id: string; assignee: string | null; expected: string | null }
  >(
    (body) =>
      http<{ question: SettledQuestion }>("/stateful-agents/feedback/assign", body).catch(
        (err: PostJsonError) => {
          throw err.status === 409 ? new Error(ASSIGNMENT_MOVED) : err;
        },
      ),
    {
      // The chip shows its own error beside it.
      meta: { silentError: true },
      onSuccess: ({ question }) => {
        for (const [key, held] of qc.getQueriesData<FeedbackList>([QK.feedbackQuestions])) {
          if (!held) continue;
          qc.setQueryData(key, {
            ...held,
            questions: held.questions.map((q) =>
              q.question_id === question.question_id
                ? { ...q, assignee: question.assignee, events: question.events }
                : q,
            ),
          });
        }
      },
      // The Agents badges count only the viewer's questions and nobody's.
      onSettled: () => {
        void qc.invalidateQueries([QK.feedbackQuestions]);
        void qc.invalidateQueries([QK.agentAttention]);
      },
    },
  );
};

type SettledQuestion = Omit<FeedbackQuestion, "project_names" | "sites">;

/** Answered or skipped is handled, from the operator's side: the row moves to Addressed
 * and each project's tab badge counts one fewer at once, before the refetches land.
 * Only queries already holding data are touched: in react-query v3 setQueryData
 * creates a missing query as a successful one with no data. */
const settleInCache = (qc: QueryClient, question: SettledQuestion) => {
  for (const [key, held] of qc.getQueriesData<FeedbackList>([QK.feedbackQuestions])) {
    if (!held) continue;
    qc.setQueryData(key, {
      ...held,
      questions: held.questions.map((q) =>
        q.question_id === question.question_id ? { ...q, ...question } : q,
      ),
    });
  }
  for (const projectId of question.project_ids) {
    const key = [QK.projectAgentStatus, projectId, null];
    const held = qc.getQueryData<ProjectAgentStatusList>(key);
    if (!held) continue;
    qc.setQueryData(key, {
      ...held,
      // An action's date move was counted as a date move, not as feedback. The
      // answered question is settled already, so isActionDateMove no longer holds.
      agents:
        question.kind === "date_move" &&
        question.subject?.item_kind === "action"
          ? held.agents
          : held.agents.map((a) =>
              a.agent === question.agent
                ? {
                    ...a,
                    feedback_waiting: Math.max(0, a.feedback_waiting - 1),
                  }
                : a,
            ),
      date_moves: Object.fromEntries(
        Object.entries(held.date_moves ?? {}).filter(
          ([, move]) => move.question_id !== question.question_id,
        ),
      ),
    });
  }
};

const refetchAfterSettling = (qc: QueryClient) => {
  void qc.invalidateQueries([QK.feedbackQuestions]);
  void qc.invalidateQueries([QK.agentAttention]);
  void qc.invalidateQueries([QK.projectAgentStatus]);
};

export const useAnswerFeedbackQuestion = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    // The answer route's question has no project_names: the list adds them.
    {
      question: SettledQuestion | null;
      conversation_id: string;
      /** A date move's answer, sent to the owning agent to take in. */
      review?: ChangeReview | null;
    },
    Error,
    {
      question_id: string;
      options: number[];
      other: string;
      actor_email: string;
      /** A date move answered with another date. */
      date?: string;
      /** A date move's answer: false leaves it for the agent's next pass. */
      wake?: boolean;
    }
  >((body) => http("/stateful-agents/feedback/answer", body), {
    onSuccess: ({ question }) => {
      if (question !== null) settleInCache(qc, question);
    },
    onSettled: (data) => {
      refetchAfterSettling(qc);
      // A question asked in a conversation is answered there, as a message.
      if (data?.conversation_id)
        void qc.invalidateQueries([QK.statefulRun, data.conversation_id]);
      // A date move answered here moves the action: its history and the dashboard follow.
      void qc.invalidateQueries([QK.actionHistory]);
      void qc.invalidateQueries([QK.dashboard]);
    },
  });
};

/** Close a question unanswered, with an optional note on why. No agent runs. */
export const useSkipFeedbackQuestion = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    { question: SettledQuestion },
    Error,
    { question_id: string; note: string; actor_email: string }
  >((body) => http("/stateful-agents/feedback/skip", body), {
    onSuccess: ({ question }) => settleInCache(qc, question),
    onSettled: () => refetchAfterSettling(qc),
  });
};

/** An Amazon answer's subject, for a question of that kind. */
export const amazonAnswerSubject = (q: FeedbackQuestion): AmazonAnswerSubject | null =>
  q.kind === "amazon_answer" && q.subject
    ? (q.subject as unknown as AmazonAnswerSubject)
    : null;
